import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";
import * as THREE from "three";

import { ClientStatus } from "@skydio/channels/src/client_status_pb";
import { DeviceNodeHealth } from "@skydio/channels/src/device_node_health_summary_pb";
import { UiVariablesPb } from "@skydio/channels/src/phone_ui_variables_pb";
import { CameraState } from "@skydio/pbtypes/pbtypes/gen/user_camera/camera_state_pb";
import { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import { movement_t } from "@skydio/lcm/types/planner/movement_t";
import { metersToFeet, secondsToMilliseconds } from "@skydio/math";
import { AccessoryEnum } from "@skydio/pbtypes/pbtypes/gen/accessories/accessory_enum_pb";
import { PortState } from "@skydio/pbtypes/pbtypes/gen/attachment_manager/port_state_pb";
import { ClientType } from "@skydio/pbtypes/pbtypes/gen/cloud_config/client_type_pb";
import { VehicleFault } from "@skydio/pbtypes/pbtypes/gen/common/fault_pb";
import { FlightPhaseEnum } from "@skydio/pbtypes/pbtypes/gen/common/flight_phase_enum_pb";
import { FeatureFlagEnum } from "@skydio/pbtypes/pbtypes/gen/flight_deck/feature_flag_enum_pb";
import { MediaMode } from "@skydio/pbtypes/pbtypes/gen/flight_deck/media_mode_pb";
import { TakeoffType } from "@skydio/pbtypes/pbtypes/gen/flight_deck/takeoff_type_pb";
import { PublicFault } from "@skydio/pbtypes/pbtypes/gen/health_monitor/public_fault_pb";
import { KeyValue } from "@skydio/pbtypes/pbtypes/gen/skills/key_value_pb";
import { UiVariableKeys } from "@skydio/pbtypes/pbtypes/gen/skills/ui_variable_keys_pb";
import { DeviceLinksSummary } from "@skydio/pbtypes/pbtypes/infrastructure/flight_connect/link_summary_pb";
import { MissionState } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission_pb";
import { FreeLookMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";
import { makeWrappedSelectors } from "@skydio/redux_util/src";
import { LinearCameraModel } from "@skydio/shared_ui/components/three/camera/linear_camera/linear_camera_model";
import { WebRTCConnectionState } from "@skydio/skybus/src/transport/types";
import { DeviceType } from "@skydio/pbtypes/pbtypes/gen/vehicle_attributes/device_type_pb";
import { CrosshairStatus } from "@skydio/pbtypes/pbtypes/vehicle/crosshair_util/crosshair_pb";
import { SubjectTrackAndFollowState } from "@skydio/pbtypes/pbtypes/vehicle/subject_follower/subject_follower_pb";

import { getIsTracking } from "state/ar/selectors";
import { getCurrentUserId } from "state/current_user/selectors";
import { getFeatureFlagValue } from "state/featureFlags";
import { getMostPreciseLocation } from "state/flight_player/location";
import {
  getCurrentPose,
  getCurrentVehicleAndCameraPoseLite,
  getCurrentVehicleStatus,
} from "state/flight_player/selectors";
import {
  getIsRunningMission,
  getMissionState,
  getSupportedFreeLookMode,
} from "state/mission/selectors";
import { getZoomFeedback } from "state/ui_feedback/selectors";
import { getCurrentEnableRapidDescent } from "state/vehicle_settings/selectors";
import { kMilliTol } from "utils/common";
import { distanceFromLatLng } from "utils/map";

import * as partialSelectors from "./partialSelectors";
import { CLOUD_SKILLS } from "./types";
import {
  augmentedKeyboardHeadingRate,
  augmentedKeyboardPitchRate,
  clampVelocityToVehicleLimits,
  CRAWL_CORRECTION_FACTOR,
  DESIRED_POSE_TIMEOUT,
  getFlightStateName,
  getGamepadMovementVector,
  getKeyboardMovementVector,
  isSkillSupported,
  MAX_PITCH_RATE_RADIANS,
  MAX_YAW_RATE_RADIANS,
  MOBILE_SL_PEER_TYPES,
  MOUSE_COMMAND_TIMEOUT,
} from "./utils";

import type { ValueType } from "@skydio/core";
import type { GamepadSettings } from "hooks/useUserOverrides";
import type { AppState } from "state/store";
import type {
  HeadingMovementDirection,
  MovementDirection,
  PitchMovementDirection,
  TeleopAction,
} from "./types";
import { StatusIconType } from "components/main/teleop/AutonomyWidget/AutonomyWidgetHeader/StatusIcon.types";
import { FlightStatusMessage } from "@skydio/pbtypes/pbtypes/gen/user_prompts/flight_status_message_pb";

export const getPilotSlice = (state: AppState) => state.pilot;
export const isVehicleConnected = (state: AppState) => state.pilot.vehicleConnection.success;
export const getMostRecentPilotUpdatedAt = (state: AppState) =>
  state.pilot.mostRecentPilot.updatedAt;
export const getIsSignaling = (state: AppState) =>
  state.pilot.vehicleConnection.status === WebRTCConnectionState.SIGNALING;

// keyboard speed related selectors
export const getVehicleSpeedSettings = (state: AppState) => state.pilot.vehicleSpeedSettings;
export const getDesiredNominalVehicleTranslationSpeed = (state: AppState) =>
  state.pilot.desiredVehicleNominalTranslationSpeed;
export const getDesiredZoomLevel = (state: AppState) => state.pilot.desiredZoomLevel;
export const getZoomCorrectionFactor = (state: AppState) => state.pilot.zoomCorrectionFactor;
export const getSpeedChangeTime = (state: AppState) => state.pilot.vehicleSpeedChangeTime;

export const {
  getClientId,
  getMostRecentPilotId,
  getIsMostRecentVehiclePilot,
  wasCommandeerRequested,
  getCurrentFlightState,
  isResting,
  isSafetyCheck,
  isReadyForTakeoff,
  isTakingOff,
  isLanding,
  isDockLanding,
  isReturningToDock,
  isReturningToLaunch,
  isInFlight,
  isAbortingLanding,
  isInPostFlight,
  isInFlyingUnknownState,
  isLandedInDockAndDockIsClosing,
  isWaitingForTakeoff,
  isReturningToSafeLanding,
  isInPerch,
  getCamNavHeadingPitch,
  getImuTCamEOPb,
  getImuTCamPb,
  getNavTGimbalCamImuPb,
  getNavTGimbalCamImu,
  getGimbalImuNavHeadingPitch,
  getImuTCamEO,
  getImuTCamIR,
  getImuRGimbalCamRdf,
  getVehiclePosition,
  getGimbalImuNavHeadingPitchDisplay,
  isReturningToPoint,
  getMaxVehicleTranslationSpeed,
  getMinVehicleTranslationSpeed,
  getNominalVehicleTranslationSpeed,
  getIsBoostActive,
  getIsCrawlActive,
  getCorrectedNominalVehicleTranslationSpeed,
} = makeWrappedSelectors(partialSelectors, getPilotSlice);

export const getHasReceivedPilot = createSelector(
  [getMostRecentPilotUpdatedAt],
  updatedAt => updatedAt !== -1
);

export const getCanSwitchRunmode = createSelector(
  isResting,
  isReadyForTakeoff,
  (resting, readyForTakeoff) => resting || readyForTakeoff
);

export const getCurrentFlightStateName = createSelector(getCurrentFlightState, flightState =>
  flightState ? getFlightStateName(flightState) : ""
);

export const wasTakeoffRequested = (state: AppState) => state.pilot.takeoffRequested;

export const isVehicleMovingAutonomously = (state: AppState) => state.pilot.movingAutonomously;

export const getPolicyCommandType = (state: AppState) => state.pilot.policyCommandType;

export const isAutonomyOff = (state: AppState) => state.pilot.isAutonomyOff;

export const isInManualTeleop = createSelector(
  isInFlight,
  getIsTracking,
  isVehicleMovingAutonomously,
  getIsRunningMission,
  (flying, tracking, isFlyingAutonomously, isFlyingMission) => {
    return flying && !tracking && !isFlyingAutonomously && !isFlyingMission;
  }
);

// display settings
export const isPointerLocked = (state: AppState) => state.pilot.mouseInput.locked;
export const getDisplayPointerLockInnerRing = (state: AppState) =>
  state.pilot.displayPointerLockInnerRing;

const getMovementKeybindings = (state: AppState) => state.pilot.movementKeybindings;
const getTeleopActionKeybindings = (state: AppState) => state.pilot.teleopActionKeybindings;
const getAllKeybindings = createSelector(
  getMovementKeybindings,
  getTeleopActionKeybindings,
  (movementKeybindings, teleopActionKeybindings) => {
    const allKeybindings = {} as Record<MovementDirection | TeleopAction, string>;
    Object.assign(allKeybindings, movementKeybindings, teleopActionKeybindings);
    return allKeybindings;
  }
);
export const canConfigureObstacleMargin = createSelector(
  isVehicleMovingAutonomously,
  movingAutonomously => !movingAutonomously
);

export const getKeybindings = createSelector(
  getAllKeybindings,
  canConfigureObstacleMargin,
  (allKeybindings, includeObstacleKey) => {
    const keysToExclude = [];

    // NOTE(duncan): Removing the live_mission_planning feature flag since it has been rolled out to
    // all orgs, but leaving in this branch on the feature flag for posterity/context. Someone who
    // knows more about keybindings may wish to clean this up.
    const liveMissionPlanning = true;

    // these are the action keys, Live Missions V2 doesn't use Redux, so don't lock those down here
    if (liveMissionPlanning) keysToExclude.push("b", "n");
    if (!includeObstacleKey) keysToExclude.push("toggleObstacleAvoidance");
    return _.omit(allKeybindings, keysToExclude);
  }
);

// don't want to show workflow keys since they're included in the skill UI (e.g mission planning)
export const getKeybindingsForDisplay = createSelector(getKeybindings, bindings =>
  _.omit(bindings, ["workflowNext", "workflowPrevious", "launchOrReturnToDock", "cancel"])
);

export const getValidInputKeys = createSelector(getKeybindings, bindings => _.values(bindings));
export const getCurrentKeyboardMovementInputs = (state: AppState) =>
  state.pilot.keyboardMovementInputs;

export const getCurrentTeleopActionInputs = (state: AppState) => state.pilot.teleopActionInputs;
export const getCurrentTeleopActionSources = (state: AppState) => state.pilot.teleopActionSources;
export const getSubjectTrackingControlMode = (state: AppState) =>
  state.pilot.teleopSubjectTrackingControlMode;
export const getCurrentActiveTeleopActionInputs = createSelector(
  getCurrentTeleopActionInputs,
  actionInputs => {
    // get all inputs with positive utimes
    return _.pickBy(actionInputs, input => input > 0) as { [k in TeleopAction]?: number };
  }
);
export const getCurrentMouseOrientation = (state: AppState) => state.pilot.mouseInput.orientation;
export const getCurrentMouseInput = (state: AppState) => state.pilot.mouseInput;
export const getLastMouseMoveTimestamp = (state: AppState) => state.pilot.mouseInput.lastMouseMove;
export const getCurrentButtonInputs = (state: AppState) => state.pilot.buttonInputs;
export const isGamepadConnected = (state: AppState) => state.pilot.gamepadConnected;
export const getGamepadMapping = (state: AppState) => state.pilot.gamepadMapping;
export const getCurrentGamepadMovementInputs = (state: AppState) =>
  state.pilot.gamepadMovementInputs;
export const getFlightId = (state: AppState) => state.pilot.flightId;
export const getFlightPhaseId = (state: AppState) => state.pilot.flightPhaseId;

// Motors are spinning during FLYING and HAND_TAKEOFF_PREP phases.
// This matches the vehicle-side MotorsSpinningPhase() check.
export const getIsMotorsNotSpinning = createSelector(getFlightPhaseId, flightPhaseId => {
  return (
    flightPhaseId !== FlightPhaseEnum.Enum.FLYING &&
    flightPhaseId !== FlightPhaseEnum.Enum.HAND_TAKEOFF_PREP
  );
});

export const getKeyboardVelocityVector = createSelector(
  getCurrentKeyboardMovementInputs,
  getCorrectedNominalVehicleTranslationSpeed,
  (keyboard, maxSpeed) => {
    return getKeyboardMovementVector(keyboard)
      .clone()
      .multiplyScalar(maxSpeed)
      .clampLength(0, maxSpeed);
  }
);

export const getKeyboardHeadingRate = createSelector(
  getCurrentKeyboardMovementInputs,
  getZoomCorrectionFactor,
  getIsCrawlActive,
  (keyboard, zoomFactor, crawlActive) => {
    const headingDirection: HeadingMovementDirection | null =
      keyboard.yawLeft > 0 ? "yawLeft" : keyboard.yawRight > 0 ? "yawRight" : null;

    return augmentedKeyboardHeadingRate(headingDirection, zoomFactor, crawlActive);
  }
);

export const getKeyboardPitchRate = createSelector(
  getCurrentKeyboardMovementInputs,
  getZoomCorrectionFactor,
  getIsCrawlActive,
  (keyboard, zoomFactor, crawlActive) => {
    const pitchDirection: PitchMovementDirection | null =
      keyboard.gimbalDown > 0 ? "gimbalDown" : keyboard.gimbalUp > 0 ? "gimbalUp" : null;

    return augmentedKeyboardPitchRate(pitchDirection, zoomFactor, crawlActive);
  }
);

export const getGamepadVelocityVector = createSelector(
  getCurrentGamepadMovementInputs,
  getMaxVehicleTranslationSpeed,
  (state: AppState, gamepadSettings: GamepadSettings) => gamepadSettings.throttleSensitivity,
  getIsBoostActive,
  getIsCrawlActive,
  (gamepad, maxSpeed, throttleSensitivity, boostActive, crawlActive) => {
    const multiplier = boostActive ? 1.0 : crawlActive ? 0.1 : throttleSensitivity;
    return getGamepadMovementVector(gamepad)
      .clone()
      .multiplyScalar(maxSpeed * multiplier)
      .clampLength(0, maxSpeed * multiplier);
  }
);
export const getGamepadHeadingRate = createSelector(
  getCurrentGamepadMovementInputs,
  (_state: AppState, gamepadSettings: GamepadSettings) => gamepadSettings.yawSensitivity,
  getZoomCorrectionFactor,
  getIsCrawlActive,
  (gamepad, yawSensitivity, zoomFactor, crawlActive) => {
    return (
      (gamepad.yawLeft - gamepad.yawRight) *
      MAX_YAW_RATE_RADIANS *
      yawSensitivity *
      zoomFactor *
      (crawlActive ? CRAWL_CORRECTION_FACTOR : 1)
    );
  }
);
export const getGamepadPitchRate = createSelector(
  getCurrentGamepadMovementInputs,
  (_state: AppState, gamepadSettings: GamepadSettings) => gamepadSettings.pitchSensitivity,
  getZoomCorrectionFactor,
  getIsCrawlActive,
  (gamepad, pitchSensitivity, zoomFactor, crawlActive) => {
    return (
      (gamepad.gimbalDown - gamepad.gimbalUp) *
      MAX_PITCH_RATE_RADIANS *
      pitchSensitivity *
      zoomFactor *
      (crawlActive ? CRAWL_CORRECTION_FACTOR : 1)
    );
  }
);

export const getWebrtcClientId = (state: AppState) => state.pilot.vehicleConnection.clientId;
export const getSessionId = (state: AppState) => state.pilot.sessionId;
export const getRunmode = (state: AppState) => state.pilot.runmode;
export const getConfigReceived = (state: AppState) => state.pilot.configReceived;

export const disableSkillSelector = createSelector(
  isTakingOff,
  isLanding,
  isReturningToDock,
  isReturningToLaunch,
  getRunmode,
  (takingOff, landing, returningToDock, returningToLaunch, runmode) =>
    takingOff || landing || returningToDock || returningToLaunch || !runmode
);

const vehicleTraslationalVelocity = (state: AppState) =>
  new THREE.Vector3(
    state.pilot.vehicleVelocity?.x,
    state.pilot.vehicleVelocity?.y,
    state.pilot.vehicleVelocity?.z
  );

export const getVehicleSpeed = createSelector(vehicleTraslationalVelocity, velocity => {
  return velocity?.length();
});
export const getDesiredPose = (state: AppState) => state.pilot.desiredPose;

export const getConnectionStats = createSelector(
  (state: AppState) => state.pilot.vehicleConnection,
  ({ downloadBitrate, uploadBitrate }) => ({ uploadBitrate, downloadBitrate })
);

export const getActiveFaults = (state: AppState) => state.pilot.activeFaults;

export const getBlockingPrompt = (state: AppState) => state.pilot.blockingPrompt;

export const getActivePrompts = (state: AppState) => state.pilot.activePromptsList;

export const hasBlockingPrompt = (state: AppState) =>
  (state.pilot.blockingPrompt?.title?.length ?? 0) > 0;

export const hasBlockingPromptDismissButton = createSelector(
  getBlockingPrompt,
  blockingPrompt =>
    (blockingPrompt?.buttonsList?.length ?? 0) > 0 &&
    !!blockingPrompt?.buttonsList?.find(button => button.id === "dismiss")
);

const createFaultActiveSelector = (fault: number) =>
  createSelector(getActiveFaults, faults => faults.includes(fault));
export const isWaitingForCamera = createFaultActiveSelector(
  PublicFault.Enum.SUBJECT_CAMERA_UNRELIABLE
);
export const isLowBatteryRtdActive = createFaultActiveSelector(
  PublicFault.Enum.BATTERY_LOW_RETURN_TO_DOCK
);

export const isLowBatteryReturnActive = createFaultActiveSelector(
  VehicleFault.Enum.BATTERY_LOW_RETURN
);

export const isBatteryCriticalActive = createFaultActiveSelector(
  VehicleFault.Enum.BATTERY_CRITICAL
);

export const isGimbalFlashlightTooHot = createFaultActiveSelector(
  VehicleFault.Enum.GIMBAL_FLASHLIGHT_TOO_HOT
);

export const isParachuteUnknownHardwareVersion = createFaultActiveSelector(
  VehicleFault.Enum.PARACHUTE_UNKNOWN_HARDWARE_VERSION
);

export const isParachuteCriticalFailureActive = createFaultActiveSelector(
  VehicleFault.Enum.PARACHUTE_CRITICAL_FAILURE
);

export const isParachuteCriticalAtsFailureActive = createFaultActiveSelector(
  VehicleFault.Enum.PARACHUTE_CRITICAL_ATS_FAILURE
);

export const isVehicleReturning = createSelector(
  isReturningToDock,
  isReturningToLaunch,
  isReturningToSafeLanding,
  isReturningToPoint,
  (returningToDock, returningToLaunch, returningToSafeLanding, returningToPoint) =>
    returningToDock || returningToLaunch || returningToSafeLanding || returningToPoint
);

export const isVehicleAirborne = createSelector(
  isInFlight,
  isTakingOff,
  isLanding,
  isAbortingLanding,
  isVehicleReturning,
  (isFlying, takingOff, landing, abortingLanding, isReturning) =>
    isFlying || takingOff || landing || abortingLanding || isReturning
);

/**
 * Determines if the ADSB alert can be suppressed.
 *
 * ADSB alerts can be suppressed if the vehicle is not flyaing
 * For precaution, we use an allowlist of states where the alert can be suppressed.
 *
 * @param isVehicleAirborne - Whether the vehicle is airborne
 * @param isResting - Whether the vehicle is resting
 * @param isLandedInDockAndDockIsClosing - Whether the vehicle is landed in dock and dock is closing
 * @param isWaitingForTakeoff - Whether the vehicle is waiting for takeoff
 * @param isInPostFlight - Whether the vehicle is in post flight
 * @returns Whether the ADSB alert can be suppressed
 */
export const canSuppressAlert = createSelector(
  isVehicleAirborne,
  isResting,
  isLandedInDockAndDockIsClosing,
  isWaitingForTakeoff,
  isInPostFlight,

  (
    isVehicleAirborne,
    isResting,
    isLandedInDockAndDockIsClosing,
    isWaitingForTakeoff,
    isInPostFlight
  ) => {
    return (
      !isVehicleAirborne && // never suppress alert if vehicle is airborne
      (isResting || isLandedInDockAndDockIsClosing || isWaitingForTakeoff || isInPostFlight)
    );
  }
);

export const getActiveSkill = (state: AppState) => state.pilot.skillState;
export const getIsWaypointsSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.WAYPOINTS
);
export const getIsManualSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.MANUAL
);
export const getIsMissionPlannerSkill = createSelector(
  getActiveSkill,
  skill => skill === CLOUD_SKILLS.MISSION_PLANNER
);

// If we are airborne, then we support all cloud skills. If on the ground, only support the ground
// compatible skills.
export const getIsActiveSkillSupported = createSelector(
  isVehicleAirborne,
  getActiveSkill,
  (airborne, activeSkill) => isSkillSupported(activeSkill, airborne)
);

const DISALLOWED_MINIMUM_OA_SKILLS = [CLOUD_SKILLS.MISSION_PLANNER, CLOUD_SKILLS.SITE_MAPPER];

export const cannotUseMinimumOA = (skill: CLOUD_SKILLS | string | undefined) =>
  DISALLOWED_MINIMUM_OA_SKILLS.includes(skill as CLOUD_SKILLS);

export const getCanUseMinimumOA = createSelector(
  getActiveSkill,
  skill => !cannotUseMinimumOA(skill)
);

export const getClientStatus = createSelector(
  wasTakeoffRequested,
  isVehicleAirborne,
  getSessionId,
  getWebrtcClientId,
  getCurrentUserId,
  getConfigReceived,
  (takeoffRequested, flying, sessionId, clientId, userId, configReceived) => {
    const clientStatus = new ClientStatus();
    // Currently, vehicle is set up to only send config once per session
    // Setting session id, basically acknowledges that we have received the config
    // and it doesn't need to be sent again
    if (configReceived) {
      clientStatus.setSessionId(sessionId);
    }
    clientStatus.setClientId(clientId);
    clientStatus.setCloudUserId(userId);
    clientStatus.setWouldAcceptPilot(true);
    clientStatus.setInForeground(true);
    clientStatus.setClientType(ClientType.Enum.WEB);
    // Set Takeoff Type for backwards compatibility with old vehicle software that expects this
    // field to be set
    clientStatus.setTakeoffType(TakeoffType.Enum.DEPRECATED_HAND_TAKEOFF);
    clientStatus.addFeatureFlags(FeatureFlagEnum.Enum.GIMBAL_INSPECTION_MODE);

    if (takeoffRequested || flying) {
      clientStatus.setMediaMode(MediaMode.Enum.FLIGHT_CONTROL);
    } else {
      clientStatus.setMediaMode(MediaMode.Enum.FLEXIBLE);
    }
    return clientStatus;
  }
);

export const getCurrentObstacleSafety = (state: AppState) => state.pilot.obstacleSetting?.safety;
export const getCurrentObstacleMarginMode = (state: AppState) =>
  state.pilot.obstacleSetting?.mode ?? VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES;

const getCameraIntrinsics = (state: AppState) => state.pilot.cameraIntrinsics;
export const getCameraCal = createSelector(
  getCameraIntrinsics,
  getZoomFeedback,
  (cameraIntrinsics, zoomFeedback) => {
    if (
      cameraIntrinsics?.focalLength === undefined ||
      cameraIntrinsics?.principalPoint === undefined ||
      zoomFeedback.zoomLevel === undefined
    ) {
      return undefined;
    }
    const zoomLevel = zoomFeedback.zoomLevel;
    // construct a camera with the effective focal length that takes into account the zoom level
    // NOTE(divyanshu): STREAM_CAMERA_CALIBRATION_PB will have the effective focal length and so
    // adjusting for zoom will no longer be needed when we have this message
    const focalLength = new THREE.Vector2()
      .fromArray(cameraIntrinsics.focalLength.dataList)
      .multiplyScalar(zoomLevel);
    const principalPoint = new THREE.Vector2().fromArray(cameraIntrinsics.principalPoint.dataList);
    // NOTE(divyanshu): `STREAM_CAMERA_CALIBRATION_PB` will have the correct image size to use
    const imageSize = principalPoint.clone().multiplyScalar(2);
    const camera = new LinearCameraModel(focalLength, principalPoint, imageSize);
    return camera;
  }
);

// Selectors for Click To Fly
export const isClickToFlyActive = (state: AppState) => state.pilot.clickToFly.active > 0;
export const clickToFlyActiveTime = (state: AppState) => state.pilot.clickToFly.active;
export const clickToFlyClickStart = (state: AppState) => state.pilot.clickToFly.clickStart;
export const clickToFlyClickEnd = (state: AppState) => state.pilot.clickToFly.clickEnd;
export const clickToFlyScrollEnd = (state: AppState) => state.pilot.clickToFly.scrollEnd;
export const getClickToFlyRay = createSelector(
  clickToFlyClickStart,
  clickToFlyScrollEnd,
  (start, end) => {
    return { start: start, end: end };
  }
);
export const getClickToFlyDisplayTrail = (state: AppState) => state.pilot.clickToFly.displayTrail;

// Determine if movement command timed out
const RESET_GIMBAL_PITCH_TIMEOUT = 5000; // we want the position command to have ehough time for the gimbal to reset to 0
const isDesiredPoseTimedOut = (state: AppState) =>
  state.pilot.desiredPose.pitch === 0 && state.pilot.desiredPose.timestamp
    ? performance.now() - state.pilot.desiredPose.timestamp > RESET_GIMBAL_PITCH_TIMEOUT
    : state.pilot.desiredPose.timestamp
      ? performance.now() - state.pilot.desiredPose.timestamp >
        secondsToMilliseconds(DESIRED_POSE_TIMEOUT)
      : true;
const isMouseInputTimedOut = (state: AppState) =>
  performance.now() - state.pilot.mouseInput.lastMouseMove >
  secondsToMilliseconds(MOUSE_COMMAND_TIMEOUT);

export const isRapidDescentActive = createSelector(
  getIsBoostActive,
  getCurrentObstacleSafety,
  getCurrentEnableRapidDescent,
  (boostActive, obstacleSafetyValue, rapidDescentEnabled) => {
    // If obstacleSafetyValue is a number > 0, obstacle avoidance is ON
    const obstacleAvoidanceOn = typeof obstacleSafetyValue === "number" && obstacleSafetyValue > 0;
    return boostActive && obstacleAvoidanceOn && rapidDescentEnabled;
  }
);

// Selectors for Consolidated Movement Commands
const getClickToFlyCommand = createSelector(isClickToFlyActive, getClickToFlyRay, (active, ray) =>
  active ? ray : undefined
);

export const getPositionControlMode = (state: AppState) => state.pilot.positionControlMode;

const getPositionCommand = createSelector(
  getPositionControlMode,
  getDesiredPose,
  isDesiredPoseTimedOut,
  (positionControlMode, desiredPose, desiredPoseTimedOut) =>
    positionControlMode == "velocity" || desiredPoseTimedOut ? undefined : desiredPose.position
);

const getVelocityCommand = createSelector(
  getKeyboardVelocityVector,
  getGamepadVelocityVector,
  isRapidDescentActive,
  (keyboardVelocity, gamepadVelocity, rapidDescentActive) => {
    const velocity = clampVelocityToVehicleLimits(
      gamepadVelocity.length() > 0 ? gamepadVelocity : keyboardVelocity,
      rapidDescentActive
    );
    const source = gamepadVelocity.length() > 0 ? "gamepad" : "keyboard";
    return { velocity, source };
  }
);
export const getHeadingControlMode = (state: AppState) => state.pilot.headingControlMode;

const getHeadingCommand = createSelector(
  getHeadingControlMode,
  getDesiredPose,
  isDesiredPoseTimedOut,
  isPointerLocked,
  getCurrentMouseInput,
  isMouseInputTimedOut,
  (
    headingControlMode,
    desiredPose,
    desiredPoseTimedOut,
    pointerLocked,
    mouseInput,
    mouseInputTimedOut
  ) => {
    if (pointerLocked) {
      return mouseInputTimedOut ? undefined : mouseInput.orientation.heading;
    }
    if (headingControlMode === "position") {
      return desiredPoseTimedOut ? undefined : desiredPose.heading;
    }
    return undefined;
  }
);
const getHeadingRateCommand = createSelector(
  getKeyboardHeadingRate,
  getGamepadHeadingRate,
  isPointerLocked,
  (keyboardHeadingRate, gamepadHeadingRate, pointerLocked) => {
    // Don't allow heading rate when pointer locked
    if (pointerLocked) {
      return { headingRate: 0.0, source: "keyboard" };
    }
    const headingRate = Math.abs(gamepadHeadingRate) > 0 ? gamepadHeadingRate : keyboardHeadingRate;
    const source = Math.abs(gamepadHeadingRate) > 0 ? "gamepad" : "keyboard";
    return { headingRate: headingRate, source: source };
  }
);
export const getPitchControlMode = (state: AppState) => state.pilot.pitchControlMode;

const getPitchCommand = createSelector(
  getPitchControlMode,
  getDesiredPose,
  isDesiredPoseTimedOut,
  getCurrentMouseInput,
  isMouseInputTimedOut,
  (pitchControlMode, desiredPose, desiredPoseTimedOut, mouseInput, mouseInputTimedOut) => {
    if (pitchControlMode === "position" && desiredPose.pitch !== undefined) {
      return desiredPoseTimedOut ? undefined : desiredPose.pitch;
    }
    // NOTE(divyanshu): The pitch mouse input is also used by the keyboard / gamepad to set pitch
    // since we don't support sending pitch rates. The integration is done in the main loop in
    // MovementManager.
    return mouseInputTimedOut ? undefined : mouseInput.orientation.pitch;
  }
);

export const getPitchRateCommand = createSelector(
  getKeyboardPitchRate,
  getGamepadPitchRate,
  isPointerLocked,
  (keyboardPitchRate, gamepadPitchRate, pointerLocked) => {
    // Don't allow heading rate when pointer locked
    if (pointerLocked) {
      return { pitchRate: 0.0, source: "keyboard" };
    }
    const pitchRate = Math.abs(gamepadPitchRate) > 0 ? gamepadPitchRate : keyboardPitchRate;
    const source = Math.abs(gamepadPitchRate) > 0 ? "gamepad" : "keyboard";
    return { pitchRate, source: source };
  }
);

// Called to set the Movement Command by MovementManager
export const getUiVariables = createSelector(
  getClickToFlyCommand,
  getPositionCommand,
  getVelocityCommand,
  getHeadingCommand,
  getHeadingRateCommand,
  getPitchCommand,
  getPitchRateCommand,
  getCorrectedNominalVehicleTranslationSpeed,
  getMaxVehicleTranslationSpeed,
  getIsBoostActive,
  getCurrentButtonInputs,
  (
    clickToFlyCommand,
    positionCommand,
    velocityCommand,
    headingCommand,
    headingRateCommand,
    pitchCommand,
    pitchRateCommand,
    nominalVehicleTranslationalSpeed,
    maxVehicleTranslationSpeed,
    isBoostActive,
    buttons
  ) => {
    const uiVariables = new UiVariablesPb();

    const addVariable = (key: ValueType<UiVariableKeys.EnumMap>, value: number) => {
      const keyVal = new KeyValue();
      keyVal.setKey(key);
      keyVal.setValue(value);
      uiVariables.addVars(keyVal);
    };

    uiVariables.setBoostCommanded(isBoostActive);

    if (clickToFlyCommand) {
      addVariable(UiVariableKeys.Enum.RAY_START_X, clickToFlyCommand.start.x);
      addVariable(UiVariableKeys.Enum.RAY_START_Y, clickToFlyCommand.start.y);
      addVariable(UiVariableKeys.Enum.RAY_START_Z, clickToFlyCommand.start.z);
      addVariable(UiVariableKeys.Enum.RAY_END_X, clickToFlyCommand.end.x);
      addVariable(UiVariableKeys.Enum.RAY_END_Y, clickToFlyCommand.end.y);
      addVariable(UiVariableKeys.Enum.RAY_END_Z, clickToFlyCommand.end.z);
      // click to fly speed should be the nominal speed set by the user
      addVariable(UiVariableKeys.Enum.MAX_SPEED, nominalVehicleTranslationalSpeed);
      addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.NAV);
      return uiVariables;
    }

    // if you have a desired_position and there is no gamepad velocity, then command one.
    // Otherwise command a velocity (from gamepad or keyboard).
    if (positionCommand !== undefined && velocityCommand.source !== "gamepad") {
      const [posX, posY, posZ] = positionCommand.toArray();
      addVariable(UiVariableKeys.Enum.POS_X, posX);
      addVariable(UiVariableKeys.Enum.POS_Y, posY);
      addVariable(UiVariableKeys.Enum.POS_Z, posZ);
      addVariable(UiVariableKeys.Enum.MAX_SPEED, maxVehicleTranslationSpeed);
      addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.NAV);
    } else {
      if (velocityCommand.velocity.length() > kMilliTol) {
        const [velX, velY, velZ] = velocityCommand.velocity.toArray();
        addVariable(UiVariableKeys.Enum.VEL_X, velX);
        addVariable(UiVariableKeys.Enum.VEL_Y, velY);
        addVariable(UiVariableKeys.Enum.VEL_Z, velZ);
        addVariable(UiVariableKeys.Enum.MAX_SPEED, maxVehicleTranslationSpeed);
        addVariable(UiVariableKeys.Enum.POS_VEL_FRAME, movement_t.VEHICLE_YAW);
      }
    }

    if (headingCommand !== undefined && headingRateCommand.source !== "gamepad") {
      addVariable(UiVariableKeys.Enum.HEADING, headingCommand);
    } else {
      if (Math.abs(headingRateCommand.headingRate) > kMilliTol) {
        addVariable(UiVariableKeys.Enum.HEADING_RATE, headingRateCommand.headingRate);
      }
    }

    if (pitchCommand !== undefined && pitchRateCommand.source !== "gamepad") {
      addVariable(UiVariableKeys.Enum.GIMBAL_PITCH, pitchCommand);
    } else {
      if (Math.abs(pitchRateCommand.pitchRate) > kMilliTol) {
        addVariable(UiVariableKeys.Enum.GIMBAL_PITCH_RATE, pitchRateCommand.pitchRate);
      }
    }

    // we need the message to be non-empty, so include this field which is a noop
    addVariable(UiVariableKeys.Enum.SMOOTH_LOOKAT, 0.0);

    if (buttons.elevation) {
      addVariable(UiVariableKeys.Enum.ELEVATION, buttons.elevation);
    }
    if (buttons.range) {
      addVariable(UiVariableKeys.Enum.RANGE, buttons.range);
    }

    return uiVariables;
  }
);

export const getReturnToDockInfo = (state: AppState) => state.pilot.returnToDockInfo;

export const getSafeLandingInfo = (state: AppState) => state.pilot.safeLandingInfo;

export const getLatestValidTargetLandingPointId = (state: AppState) =>
  state.pilot.latestValidTargetLandingPointId;

export const getGimbalFlashlightStatus = (state: AppState) => state.pilot.gimbalFlashlightStatus;

export const isThirdPersonViewActive = (state: AppState) => state.pilot.thirdPersonViewActive;

export const getStreamCameraCalibration = (state: AppState) => state.pilot.streamCameraCalibration;

export const getLandingPadTracker = (state: AppState) => state.pilot.landingPadTracker;

export const getRssi = (state: AppState) => state.pilot.rssi;

export const getVehicleAmbassadorStatusLastUpdated = (state: AppState) =>
  state.pilot.vehicleAmbassadorStatusLastUpdated;

export const getDefaultOrbitInterceptMode = (state: AppState) =>
  state.pilot.defaultOrbitInterceptMode;

export const getDefaultOrbitDirection = (state: AppState) => state.pilot.defaultOrbitDirection;

export const getDefaultPathBetweenPointsMode = (state: AppState) =>
  state.pilot.defaultPathBetweenPointsMode;

export const getDefaultPathBetweenPointsModeIsPathfinder = (state: AppState) =>
  state.pilot.defaultPathBetweenPointsMode === "pathfinder";

export const getDefaultLookAtPointActive = (state: AppState) =>
  state.pilot.defaultLookAtPointActive;

export const getRampHeightModeActive = (state: AppState) => state.pilot.rampHeightModeActive;

export const isPointerLockAllowed = createSelector(
  isInManualTeleop,
  getSupportedFreeLookMode,
  isVehicleReturning,
  getMissionState,
  (manualTeleop, supportedFreeLookMode, vehicleReturning, missionState) => {
    // allow pointer lock if we're in manual teleop or running a mission
    const freeLookAllowsPanAndTilt =
      supportedFreeLookMode !== undefined &&
      supportedFreeLookMode.valueOf() === FreeLookMode_Enum.PAN_AND_TILT;
    const executingPostMissionActions = (
      [
        MissionState.MissionStateEnum.COMPLETED,
        MissionState.MissionStateEnum.POST_MISSION_TASK,
        MissionState.MissionStateEnum.POST_MISSION_ACTION,
      ] as ValueType<MissionState.MissionStateEnumMap>[]
    ).includes(missionState.stateEnum);
    return (
      manualTeleop ||
      ((freeLookAllowsPanAndTilt || executingPostMissionActions) && !vehicleReturning)
    );
  }
);

export const getUserCameraAutoRegionState = (state: AppState) =>
  state.pilot.userCameraAutoRegionState;

export const getUserCameraRegionStateAutoFocus = createSelector(
  getUserCameraAutoRegionState,
  userCameraAutoRegionState => userCameraAutoRegionState?.autofocusMode
);

export const getUserCameraAeRegion = createSelector(
  getUserCameraAutoRegionState,
  userCameraAutoRegionState => userCameraAutoRegionState?.aeRegionList
);

export const getUserCameraAfRegion = createSelector(
  getUserCameraAutoRegionState,
  userCameraAutoRegionState => userCameraAutoRegionState?.afRegionList
);

export const getLookAtFinalPointActive = (state: AppState) => state.pilot.lookAtFinalPointActive;

export const getAttachmentPortStatus = (state: AppState) => state.pilot.attachmentPortStatus;

export const getUserCameraStatus = (state: AppState) => state.pilot.userCameraStatus;

export const isUserCameraRecording = createSelector(getUserCameraStatus, userCameraStatus =>
  // NOTE(jesse): Vehicles older than Cayley version dont send the user camera status, in those
  // cases, return undefined so we can handle fallback behavior when using it
  userCameraStatus?.getState() == undefined
    ? undefined
    : userCameraStatus?.getState() == CameraState.Enum.RECORDING
);

/**
 * Calculate the height of the vehicle above the landing pad based on the nav frame positions
 * of the landing pad and the vehicle's pose.
 */
export const getCurrentHeightInFtAboveLandingPad = createSelector(
  getCurrentPose,
  getLandingPadTracker,
  (pose, landingPadTracker) => {
    // NOTE(parth): This is technically not exactly what you want -- nav_T_root is the position of the tags,
    // and nav_T_vehicle (the vehicle pose) is the position of the vehicle IMU. As a result this function will
    // return 12cm when you are on the platform. However, it looks like we are not sending the frontend
    // nav_T_vehicle_target, which is what you really want, and this is good enough for what you are using it
    // for currently
    const landingPadPosition = landingPadTracker?.focusTrack?.track?.navTRoot?.position?.dataList;
    const vehiclePosition = pose?.position;

    if (!landingPadPosition || !vehiclePosition) {
      return undefined;
    }

    const dockHeight = new THREE.Vector3().fromArray(landingPadPosition);
    const vehicleHeight = new THREE.Vector3().fromArray(vehiclePosition);

    return Math.round(metersToFeet(vehicleHeight.z - dockHeight.z));
  }
);

export const isAnyAttachmentConnected = createSelector(
  getAttachmentPortStatus,
  attachmentPortStatus => {
    return attachmentPortStatus?.portDataMap?.some(
      portData => portData[1].portState === PortState.Enum.CONNECTED
    );
  }
);
export const getSpotlightSystemStatus = (state: AppState) => state.pilot.spotlightSystemStatus;
export const getDeviceLinksSummary = (deviceId: string) =>
  createSelector(
    (state: AppState) => state.pilot.deviceLinksSummary,
    deviceLinksSummary => deviceLinksSummary[deviceId] ?? new DeviceLinksSummary()
  );
export const getIsMobileSLOnly = (deviceId: string) =>
  createSelector(
    (state: AppState) => state.pilot.deviceLinksSummary,
    (deviceLinksSummary): boolean => {
      const deviceLink = deviceLinksSummary[deviceId];
      // If device doesn't exist, return false
      if (!deviceLink) {
        return false;
      }
      const linkSummaries = deviceLink.getLinkSummariesList() ?? [];
      // Check if all enabled links are mobile SL peers
      let hasActiveMobileLink = false;
      const noNonMobileLinks =
        linkSummaries.every(linkSummary => {
          if (!linkSummary.getEnabled()) {
            return true;
          }
          const primaryPeerDeviceType =
            linkSummary.getWifiSummary()?.getPrimaryPeerDeviceType() ?? DeviceType.Enum.UNKNOWN;
          if (MOBILE_SL_PEER_TYPES.has(primaryPeerDeviceType)) {
            hasActiveMobileLink = true;
            return true;
          }
          return false;
        }) ?? false;
      return noNonMobileLinks && hasActiveMobileLink;
    }
  );
export const getDeviceNodeHealth = (deviceId: string) =>
  createSelector(
    (state: AppState) => state.pilot.deviceNodeHealth,
    deviceNodeHealth => deviceNodeHealth[deviceId] ?? new DeviceNodeHealth()
  );

export const isSpotlightAttached = createSelector(getAttachmentPortStatus, attachmentPortStatus => {
  return attachmentPortStatus?.portDataMap?.find(
    portData =>
      (
        [
          AccessoryEnum.Enum.R47_SPOTLIGHT_EVT,
          AccessoryEnum.Enum.R47_SPOTLIGHT_PVT,
        ] as ValueType<AccessoryEnum.EnumMap>[]
      ).includes(portData[1].type) && portData[1].portState === PortState.Enum.CONNECTED
  );
});

export const isSpotlightThrottled = createFaultActiveSelector(
  VehicleFault.Enum.ATTACHMENT_SPOTLIGHT_BRIGHTNESS_THROTTLING
);

export const isSpotlightOverheated = createFaultActiveSelector(
  VehicleFault.Enum.ATTACHMENT_SPOTLIGHT_ABOVE_TOUCH_TEMP
);

export const getSpeakerMicStatus = (state: AppState) => state.pilot.speakerMicStatus;

export const getSpeakerMicAudioFiles = (state: AppState) =>
  state.pilot.speakerMicAudioFiles?.getAudioFilesList();

// DroneSense Mission Selection integration
export const getDroneSenseRequestStatus = (state: AppState) =>
  state.pilot.droneSense?.requestStatus;
export const getDroneSenseMissions = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS" ? state.pilot.droneSense.missions : [];
export const getSelectedDroneSenseMissionId = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS"
    ? state.pilot.droneSense?.selectedMissionId
    : undefined;
export const getSelectedDroneSenseMission = createSelector(
  getDroneSenseMissions,
  getSelectedDroneSenseMissionId,
  (missions, selectedMissionId) => {
    return missions.find(mission => mission.missionId === selectedMissionId);
  }
);
export const getDroneSenseCallSign = (state: AppState) =>
  state.pilot.droneSense.requestStatus === "SUCCESS" ? state.pilot.droneSense.callSign : "";
export const getLastKnownFlyingFlightId = (state: AppState) => state.pilot.lastKnownFlyingFlightId;

export const getFarAwayFromLaunchPoint = (state: AppState) => state.pilot.farAwayFromLaunchPoint;
export const getFarAwayFromLaunchPointDismissed = (state: AppState) =>
  state.pilot.farAwayFromLaunchPointDismissed;

export const getFlyToPointStandoffDistance = (state: AppState) =>
  state.pilot.flyToPointStandoffDistance;

export const getParachuteStatus = (state: AppState) => state.pilot.parachuteStatus;

export const getDropperDeployCount = (state: AppState) =>
  state.pilot.dropperStatus?.getDeployCount();

export const getDropperState = (state: AppState) => state.pilot.dropperStatus?.getState();

export const getBlockingFaultsListPb = (state: AppState) => state.pilot.blockingFaultsPb;

export const getIsSubjectTrackingOld = (state: AppState) =>
  (state.pilot.subjectTrackingIndex ?? -1) > -1;

export const getIsSubjectTracking = (state: AppState) =>
  (state.pilot.subjectState ?? SubjectTrackAndFollowState.Enum.INACTIVE) !==
  SubjectTrackAndFollowState.Enum.INACTIVE;

export const getIsSubjectFollowing = (state: AppState) =>
  state.pilot.subjectState === SubjectTrackAndFollowState.Enum.FOLLOWING;

export const getSubjectFollowBlockedReason = (state: AppState) =>
  state.pilot.subjectFollowBlockedReason;

export const getAutonomyWidgetStatus = (state: AppState) => state.pilot.autonomyWidgetStatus;

export const getAutonomyWidgetStatusText = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus =>
    autonomyWidgetStatus?.statusText || autonomyWidgetStatus?.statusTitle || ""
);

export const getAutonomyWidgetShow = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.show
);

export const getAutonomyWidgetStatusTitle = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.statusTitle
);

export const getAutonomyWidgetStatusTextOnly = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.statusText
);

export const getAutonomyWidgetWaypointIndex = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.waypointIndex
);

export const getAutonomyWidgetNumWaypoints = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.numWaypoints
);

export const getAutonomyWidgetIsMissionPaused = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.isMissionPaused
);

export const getAutonomyWidgetRemainingTimeEstimate = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.remainingTimeEstimate
);

export const getAutonomyWidgetRemainingDistanceEstimate = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.remainingDistanceEstimate
);

export const getAutonomyWidgetMapCapture = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.mapCapture
);

export const getAutonomyWidgetProgressRatio = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.progressRatio
);

export const getAutonomyWidgetMissionName = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.missionName
);

export const getAutonomyWidgetIsMissionActive = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.isMissionActive
);

export const getAutonomyWidgetShowDropdown = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.showDropdown
);

export const getAutonomyWidgetStatusType = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.statusType
);

export const getAutonomyWidgetIsReturning = createSelector(
  getAutonomyWidgetStatusType,
  autonomyWidgetStatusType =>
    autonomyWidgetStatusType === StatusIconType.ReturningToDock ||
    autonomyWidgetStatusType === StatusIconType.ReturningToLaunch ||
    autonomyWidgetStatusType === StatusIconType.ReturningToSafePoint
);

export const getAutonomyWidgetActionButtonType = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.actionButtonType
);

export const getAutonomyWidgetActionButtonTooltipText = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.actionButtonTooltipText
);

export const getAutonomyWidgetIsCompact = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.isCompact
);

export const getAutonomyWidgetIsFlyToPointMission = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.isFlyToPointMission
);

export const getAutonomyWidgetIsUsingPathfinder = createSelector(
  getAutonomyWidgetStatus,
  autonomyWidgetStatus => autonomyWidgetStatus?.isUsingPathfinder
);

export const getIsTurtleModeActive = createFaultActiveSelector(
  VehicleFault.Enum.TURTLE_MODE_ACTIVE
);

export const getTurtleModeRecommendation = (state: AppState) =>
  state.pilot.turtleModeRecommendation;

export const getCellularModemStates = (state: AppState) => {
  return state.pilot.cellularModemStates;
};

// Modems that are part of an attachment will have an attachment serial
export const getA22Modems = createSelector(getCellularModemStates, cellularModemStates => {
  return cellularModemStates?.getStatesList().filter(state => !!state.getAttachmentSerial());
});

export const getActiveA22SimSlotIndex = createSelector(getA22Modems, a22Modems => {
  const selectedModem = a22Modems?.find(modem => modem.getSelectedSimIndex() !== -1);
  if (!selectedModem) {
    return undefined;
  }
  return selectedModem.getSelectedSimIndex();
});

export const getA22ModemSourceLink = createSelector(getA22Modems, a22Modems => {
  const selectedModem = a22Modems?.find(modem => modem.getSelectedSimIndex() !== -1);
  if (!selectedModem) {
    return undefined;
  }
  return selectedModem.getSourceLink();
});

export const getA22SimOptions = (state: AppState) => state.pilot.a22SimOptions;

// Vehicle built-in modems with multiple SIM slots (no attachment serial, e.g. R71)
export const getVehicleModems = createSelector(getCellularModemStates, cellularModemStates => {
  return cellularModemStates
    ?.getStatesList()
    .filter(state => !state.getAttachmentSerial() && state.getSimSlotsList().length > 1);
});

export const getActiveVehicleSimSlotIndex = createSelector(getVehicleModems, vehicleModems => {
  const selectedModem = vehicleModems?.find(modem => modem.getSelectedSimIndex() !== -1);
  if (!selectedModem) {
    return undefined;
  }
  return selectedModem.getSelectedSimIndex();
});

export const getVehicleModemSourceLink = createSelector(getVehicleModems, vehicleModems => {
  const selectedModem = vehicleModems?.find(modem => modem.getSelectedSimIndex() !== -1);
  if (!selectedModem) {
    return undefined;
  }
  return selectedModem.getSourceLink();
});

export const getVehicleSimOptions = (state: AppState) => state.pilot.vehicleSimOptions;

export const getUserAutonomousFlightSpeed = (state: AppState) =>
  state.pilot.autonomousFlightSpeed.userSetting;

export const getAutonomousFlightSpeed = (state: AppState) =>
  state.pilot.autonomousFlightSpeed.userSetting ?? state.pilot.autonomousFlightSpeed.policySetting;

export const getRemoteIdBroadcasting = (state: AppState) => state.pilot.remoteIdBroadcasting;

export const getMostRecentPilotCloudUserId = (state: AppState) =>
  state.pilot.mostRecentPilot.pilotCloudUserId;

export const isDroneInStandby = createSelector(getActivePrompts, activePrompts => {
  return activePrompts.some(
    prompt => prompt.messageEnum === FlightStatusMessage.Enum.DRONE_IN_STANDBY
  );
});

export const getVehicleCrosshairPosition = (state: AppState) =>
  state.pilot.vehicleCrosshairPosition;

/**
 * Computes the 3D slant range from the vehicle to the vehicle-provided crosshair
 * point when valid DTM-based crosshair data is available (gated by the
 * "aglFromVehicleRfd" feature flag). Returns null when the feature flag is off,
 * crosshair data is absent/invalid, or vehicle position is unavailable.
 */
export const getVehicleCrosshairDistance = createSelector(
  (state: AppState) => getFeatureFlagValue(state, "aglFromVehicleRfd"),
  getVehicleCrosshairPosition,
  getCurrentVehicleAndCameraPoseLite,
  getCurrentVehicleStatus,
  (
    useVehicleCrosshair,
    crosshairPosition,
    currentPoseLite,
    currentVehicleStatus
  ): number | null => {
    if (
      !useVehicleCrosshair ||
      !crosshairPosition ||
      crosshairPosition.status !== CrosshairStatus.Enum["DATA_IS_VALID"]
    ) {
      return null;
    }

    const vehiclePosition = getMostPreciseLocation(currentPoseLite, currentVehicleStatus);
    if (!vehiclePosition) {
      return null;
    }

    const vehicleAltMsl = currentVehicleStatus?.vehicleGps?.altitude ?? 0;

    const horizontalDistance = distanceFromLatLng(
      [vehiclePosition.lat, vehiclePosition.lng],
      [crosshairPosition.lat, crosshairPosition.lng]
    );
    const altDiff = vehicleAltMsl - (crosshairPosition.altMsl ?? 0);
    return Math.sqrt(horizontalDistance ** 2 + altDiff ** 2);
  }
);

export const getParaverseScenarioStatus = (state: AppState) => state.pilot.paraverseScenarioStatus;

/**
 * Scenario widget visibility: show as long as we have status data from the topic.
 * Feature flag gating is done at the component level (enableParaverseArcadeCreation).
 */
export const getParaverseScenarioWidgetVisible = createSelector(
  [getIsMostRecentVehiclePilot, isVehicleConnected, getParaverseScenarioStatus],
  (isPilot, connected, status) => isPilot && connected && !!status
);
